import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { Register } from '@app/utils/type-registry';
import {
  ExpansionPanelTableInput,
  ExpansionPanelTableOutput,
  isExpression,
} from '@trackback/widgets';
import { map, switchMap } from 'rxjs/operators';
import { BaseWidgetComponent } from '@app/widgets/base-widget.component';
import { DynamicWidgetDirective } from '@app/directives/dynamic-widget.directive';
import { ParsePipe } from '@app/pipes/parse.pipe';
import { MatSortModule } from '@angular/material/sort';
import { isString } from 'lodash-es';
import { combineLatest, of } from 'rxjs';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { AttachPipe } from '@app/pipes/attach.pipe';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';

@Component({
  selector: 'tb-expansion-panel-table',
  templateUrl: './expansion-panel-table.component.html',
  styleUrls: ['./expansion-panel-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    MatExpansionModule,
    ParsePipe,
    DynamicWidgetDirective,
    MatSortModule,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatCell,
    MatCellDef,
    AttachPipe,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatIcon,
    MatIconButton,
    MatTable,
  ],
})
@Register('expansionpaneltable')
export class ExpansionPanelTableComponent extends BaseWidgetComponent<
  ExpansionPanelTableInput,
  ExpansionPanelTableOutput
> {
  headers$ = this.input$.pipe(
    switchMap(input => {
      if (isExpression(input.tableHeader)) {
        return this.parse(input.tableHeader);
      } else {
        return of(input.tableHeader);
      }
    })
  );

  columns$ = this.input$.pipe(
    switchMap(input => {
      if (isExpression(input.tableColumn)) {
        return this.parse(input.tableColumn);
      } else {
        return of(input.tableColumn);
      }
    })
  );

  columnDefs$ = combineLatest([this.headers$, this.columns$]).pipe(
    map(([headers, columns]) => {
      const result: Array<{
        header: (typeof headers)[number];
        column: (typeof columns)[number];
      }> = [];

      for (let i = 0; i < headers.length; i++) {
        result.push({
          header: headers[i],
          column: columns[i],
        });
      }

      return result;
    })
  );

  columnsToDisplay$ = this.columnDefs$.pipe(
    map(colDefs => [...colDefs.map((_, idx) => String(idx)), 'expand'])
  );

  expandedElements = signal([]);

  isExpanded(element: unknown) {
    return this.expandedElements().includes(element);
  }

  toggle(element: unknown) {
    if (this.input.multiple ?? true) {
      if (this.isExpanded(element)) {
        this.expandedElements.update(prev => prev.filter(it => it !== element));
      } else {
        this.expandedElements.update(prev => [...prev, element]);
      }
    } else {
      if (this.isExpanded(element)) {
        this.expandedElements.set([]);
      } else {
        this.expandedElements.set([element]);
      }
    }
  }

  protected readonly isString = isString;
  protected readonly isExpression = isExpression;
}
